import { ValidationResult, ValidationError, ValidationWarning, ConflictRecord } from '../types';

// Data transformation utilities
export const transformDataForExport = (data: any[], fields?: string[]): any[] => {
  if (!fields || fields.length === 0) {
    return data;
  }
  
  return data.map(row => {
    const transformedRow: any = {};
    fields.forEach(field => {
      if (row.hasOwnProperty(field)) {
        transformedRow[field] = row[field];
      }
    });
    return transformedRow;
  });
};

export const transformDataForImport = (data: any[], mapping?: Record<string, string>): any[] => {
  if (!mapping) {
    return data;
  }
  
  return data.map(row => {
    const transformedRow: any = {};
    Object.entries(mapping).forEach(([sourceField, targetField]) => {
      if (row.hasOwnProperty(sourceField)) {
        transformedRow[targetField] = row[sourceField];
      }
    });
    return transformedRow;
  });
};

// Data comparison utilities
export const compareRecords = (existing: any, incoming: any, fields: string[]): ConflictRecord[] => {
  const conflicts: ConflictRecord[] = [];
  
  fields.forEach(field => {
    const existingValue = existing[field];
    const incomingValue = incoming[field];
    
    if (existingValue !== incomingValue) {
      conflicts.push({
        field,
        currentValue: existingValue,
        incomingValue: incomingValue,
        row: 0, // Will be set by caller
        conflictType: 'value_mismatch'
      });
    }
  });
  
  return conflicts;
};

export const findNewFields = (existing: any, incoming: any): ConflictRecord[] => {
  const conflicts: ConflictRecord[] = [];
  const existingKeys = Object.keys(existing);
  const incomingKeys = Object.keys(incoming);
  
  incomingKeys.forEach(key => {
    if (!existingKeys.includes(key)) {
      conflicts.push({
        field: key,
        currentValue: undefined,
        incomingValue: incoming[key],
        row: 0,
        conflictType: 'new_field'
      });
    }
  });
  
  return conflicts;
};

export const findMissingFields = (existing: any, incoming: any): ConflictRecord[] => {
  const conflicts: ConflictRecord[] = [];
  const existingKeys = Object.keys(existing);
  const incomingKeys = Object.keys(incoming);
  
  existingKeys.forEach(key => {
    if (!incomingKeys.includes(key)) {
      conflicts.push({
        field: key,
        currentValue: existing[key],
        incomingValue: undefined,
        row: 0,
        conflictType: 'missing_field'
      });
    }
  });
  
  return conflicts;
};

// Data validation utilities
export const validateRequiredFields = (data: any[], requiredFields: string[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  data.forEach((row, index) => {
    requiredFields.forEach(field => {
      if (!row[field] || (typeof row[field] === 'string' && row[field].trim() === '')) {
        errors.push({
          field,
          message: `${field} is required`,
          row: index + 1
        });
      }
    });
  });
  
  return errors;
};

export const validateUniqueFields = (data: any[], uniqueFields: string[]): ValidationError[] => {
  const errors: ValidationError[] = [];
  const seen = new Map<string, Set<string>>();
  
  uniqueFields.forEach(field => {
    seen.set(field, new Set());
  });
  
  data.forEach((row, index) => {
    uniqueFields.forEach(field => {
      const value = row[field];
      if (value !== undefined && value !== null && value !== '') {
        const fieldSeen = seen.get(field);
        if (fieldSeen && fieldSeen.has(String(value))) {
          errors.push({
            field,
            message: `${field} must be unique. Duplicate value: ${value}`,
            row: index + 1
          });
        } else if (fieldSeen) {
          fieldSeen.add(String(value));
        }
      }
    });
  });
  
  return errors;
};

export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};

export const validateDate = (date: string): boolean => {
  const dateObj = new Date(date);
  return dateObj instanceof Date && !isNaN(dateObj.getTime());
};

export const validateNumber = (value: any, min?: number, max?: number): boolean => {
  const num = Number(value);
  if (isNaN(num)) return false;
  if (min !== undefined && num < min) return false;
  if (max !== undefined && num > max) return false;
  return true;
};

// Data formatting utilities
export const formatDate = (date: string | Date, format: 'ISO' | 'US' | 'EU' = 'ISO'): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  if (isNaN(dateObj.getTime())) {
    return '';
  }
  
  try {
    switch (format) {
      case 'US':
        return dateObj.toLocaleDateString('en-US');
      case 'EU':
        return dateObj.toLocaleDateString('en-GB');
      case 'ISO':
      default:
        return dateObj.toISOString().split('T')[0];
    }
  } catch (error) {
    console.warn('Error formatting date:', error);
    return '';
  }
};

export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export const formatNumber = (value: number, decimals: number = 2): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
};

// Data cleaning utilities
export const cleanString = (str: string): string => {
  return str.trim().replace(/\s+/g, ' ');
};

export const cleanObject = (obj: any): any => {
  const cleaned: any = {};
  
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value === 'string') {
        cleaned[key] = cleanString(value);
      } else {
        cleaned[key] = value;
      }
    }
  });
  
  return cleaned;
};

export const removeEmptyFields = (obj: any): any => {
  const cleaned: any = {};
  
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  
  return cleaned;
};

// Data grouping utilities
export const groupBy = (data: any[], key: string): Record<string, any[]> => {
  return data.reduce((groups, item) => {
    const group = item[key];
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(item);
    return groups;
  }, {} as Record<string, any[]>);
};

export const sortBy = (data: any[], key: string, direction: 'asc' | 'desc' = 'asc'): any[] => {
  return [...data].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];
    
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

// Data statistics utilities
export const calculateStats = (data: any[], numericFields: string[]): Record<string, any> => {
  const stats: Record<string, any> = {};
  
  numericFields.forEach(field => {
    const values = data
      .map(row => Number(row[field]))
      .filter(val => !isNaN(val));
    
    if (values.length > 0) {
      stats[field] = {
        count: values.length,
        sum: values.reduce((sum, val) => sum + val, 0),
        average: values.reduce((sum, val) => sum + val, 0) / values.length,
        min: Math.min(...values),
        max: Math.max(...values),
        median: values.sort((a, b) => a - b)[Math.floor(values.length / 2)]
      };
    }
  });
  
  return stats;
};

// Data export utilities
export const prepareDataForCSV = (data: any[]): string => {
  if (data.length === 0) return '';
  
  const headers = Object.keys(data[0]);
  const csvRows = [headers.join(',')];
  
  data.forEach(row => {
    const values = headers.map(header => {
      const value = row[header];
      if (value === null || value === undefined) return '';
      if (typeof value === 'string' && value.includes(',')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    });
    csvRows.push(values.join(','));
  });
  
  return csvRows.join('\n');
};

export const prepareDataForJSON = (data: any[]): string => {
  return JSON.stringify(data, null, 2);
};

// Remove empty string fields from objects (for import - empty strings should not be imported)
export const removeEmptyStringFields = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }

  // If it's an array, process each item
  if (Array.isArray(data)) {
    return data.map(item => removeEmptyStringFields(item));
  }

  // If it's an object, remove empty string fields and recursively process nested objects
  if (typeof data === 'object') {
    const cleaned: any = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key];
        
        // Skip empty strings
        if (value === '') {
          continue;
        }
        
        // Recursively process nested objects and arrays
        cleaned[key] = removeEmptyStringFields(value);
      }
    }
    return cleaned;
  }

  // Return primitive values as-is
  return data;
};

// Parse JSON strings in imported data (for CSV imports where objects/arrays come as strings)
export const parseJsonStringsInData = (data: any): any => {
  if (data === null || data === undefined) {
    return data;
  }

  // If it's an array, process each item
  if (Array.isArray(data)) {
    return data.map(item => parseJsonStringsInData(item));
  }

  // If it's an object, process each property
  if (typeof data === 'object') {
    const parsed: any = {};
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        parsed[key] = parseJsonStringsInData(data[key]);
      }
    }
    return parsed;
  }

  // If it's a string, try to parse it as JSON
  if (typeof data === 'string') {
    let trimmed = data.trim();
    
    // Skip empty strings
    if (trimmed === '') {
      return data;
    }

    const tryParseJsonCandidate = (candidate: string): any => {
      try {
        const parsed = JSON.parse(candidate);
        return parseJsonStringsInData(parsed);
      } catch {
        return null;
      }
    };

    const tryExtractJsonBlock = (candidate: string): any => {
      const objectStart = candidate.indexOf('{');
      const objectEnd = candidate.lastIndexOf('}');
      if (objectStart !== -1 && objectEnd > objectStart) {
        const objectSlice = candidate.slice(objectStart, objectEnd + 1);
        const parsedObject = tryParseJsonCandidate(objectSlice);
        if (parsedObject !== null) return parsedObject;
      }

      const arrayStart = candidate.indexOf('[');
      const arrayEnd = candidate.lastIndexOf(']');
      if (arrayStart !== -1 && arrayEnd > arrayStart) {
        const arraySlice = candidate.slice(arrayStart, arrayEnd + 1);
        const parsedArray = tryParseJsonCandidate(arraySlice);
        if (parsedArray !== null) return parsedArray;
      }

      return null;
    };

    // Try robust decoding for wrapped/escaped JSON strings (multiple passes).
    for (let i = 0; i < 4; i++) {
      // Direct JSON parse attempt.
      const parsedDirect = tryParseJsonCandidate(trimmed);
      if (parsedDirect !== null) {
        return parsedDirect;
      }

      // Extract object/array block from noisy wrappers and parse.
      const extractedParsed = tryExtractJsonBlock(trimmed);
      if (extractedParsed !== null) {
        return extractedParsed;
      }

      // Remove surrounding quotes if present.
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
          (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        trimmed = trimmed.slice(1, -1).trim();
      }

      // Unescape common escaped sequences.
      trimmed = trimmed
        .replace(/\\"/g, '"')
        .replace(/\\n/g, '\n')
        .replace(/\\r/g, '\r')
        .replace(/\\t/g, '\t')
        .replace(/\\\\/g, '\\')
        .trim();
    }

    // Handle boolean strings (case-insensitive) - CSV often exports as "TRUE"/"FALSE"
    const lowerTrimmed = trimmed.toLowerCase();
    if (lowerTrimmed === 'true' || lowerTrimmed === '1' || lowerTrimmed === 'yes') {
      return true;
    }
    if (lowerTrimmed === 'false' || lowerTrimmed === '0' || lowerTrimmed === 'no') {
      return false;
    }

    // Handle null strings
    if (trimmed === 'null') {
      return null;
    }

    // Try to parse as JSON if it looks like JSON (starts with [ or {)
    if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || 
        (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
      try {
        const parsed = JSON.parse(trimmed);
        // Recursively parse nested JSON strings
        return parseJsonStringsInData(parsed);
      } catch (e) {
        // If parsing fails, return original string
        return data;
      }
    }

    // Try to parse as number if it's a numeric string
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const num = Number(trimmed);
      if (!isNaN(num)) {
        return num;
      }
    }
  }

  // Return as-is if no parsing needed
  return data;
};